import React from 'react'
import { Pre, ProgressBar, Intent } from '@blueprintjs/core'
import { Card, Elevation } from '../ui/card'
import { ChatMessage as ChatMessageType, DSLResponse } from '../../types/copilot'
import { Button } from '../ui/button'

interface ChatMessageProps {
    message: ChatMessageType
    onSuggestionClick?: (query: string) => void
}

function parseAssistantResponse(content: string): DSLResponse | null {
    try {
        // Try to parse as-is first
        const parsed = JSON.parse(content)
        if (parsed.dsl_query) {
            return parsed
        }
    } catch {
        // If direct parsing fails, try to extract JSON from markdown code blocks
        const jsonMatch = content.match(/```json\s*([\s\S]*?)\s*```/)
        if (jsonMatch) {
            try {
                let jsonContent = jsonMatch[1]
                // Try to fix common JSON corruption issues
                jsonContent = fixCommonJsonIssues(jsonContent)
                const parsed = JSON.parse(jsonContent)
                if (parsed.dsl_query) {
                    return parsed
                }
            } catch {
                // Still couldn't parse
            }
        }

        // Try to find JSON-like content without markdown wrapper
        const jsonPattern = /\{[\s\S]*"dsl_query"[\s\S]*\}/
        const match = content.match(jsonPattern)
        if (match) {
            try {
                let jsonContent = match[0]
                // Try to fix common JSON corruption issues
                jsonContent = fixCommonJsonIssues(jsonContent)
                const parsed = JSON.parse(jsonContent)
                if (parsed.dsl_query) {
                    return parsed
                }
            } catch {
                // Still couldn't parse
            }
        }
    }
    return null
}

function fixCommonJsonIssues(jsonContent: string): string {
    // Fix corrupted boolean fields
    jsonContent = jsonContent.replace(/"uses_[^"]*",/g, '"uses_variables": false,')
    jsonContent = jsonContent.replace(/"creates_[^"]*",/g, '"creates_custom_properties": false,')

    // Fix trailing commas before closing braces/brackets
    jsonContent = jsonContent.replace(/,(\s*[}\]])/g, '$1')

    // Fix incomplete or corrupted field names
    jsonContent = jsonContent.replace(/"uses_\w*":\s*[^,}]+,/g, '"uses_variables": false,')
    jsonContent = jsonContent.replace(
        /"creates_\w*":\s*[^,}]+,/g,
        '"creates_custom_properties": false,',
    )

    return jsonContent
}

export function ChatMessage({ message, onSuggestionClick }: ChatMessageProps) {
    const isUser = message.role === 'user'
    const parsedResponse = !isUser ? parseAssistantResponse(message.content) : null

    return (
        <div className={`flex w-full mb-4 ${isUser ? 'justify-end' : 'justify-start'}`}>
            <Card
                elevation={Elevation.ONE}
                className={`max-w-[80%] ${isUser ? 'bg-blue-50' : 'bg-gray-50'}`}
                style={{ padding: '16px' }}
            >
                <div className="text-xs text-gray-600 mb-2">{isUser ? 'You' : 'Assistant'}</div>

                {parsedResponse ? (
                    <div className="space-y-4">
                        <div>
                            <h4 className="font-semibold mb-2 text-sm">Recipe Trigger:</h4>
                            <Pre className="text-xs bg-gray-100 p-3 rounded overflow-x-auto">
                                {parsedResponse.dsl_query}
                            </Pre>
                        </div>

                        {parsedResponse.reasoning && (
                            <div>
                                <h4 className="font-semibold mb-2 text-sm">Reasoning:</h4>
                                <p className="text-sm text-gray-700">{parsedResponse.reasoning}</p>
                            </div>
                        )}

                        {parsedResponse.confidence !== undefined && (
                            <div>
                                <h4 className="font-semibold mb-2 text-sm">Confidence Score:</h4>
                                <div className="flex items-center space-x-2">
                                    <ProgressBar
                                        value={parsedResponse.confidence}
                                        intent={Intent.PRIMARY}
                                        className="w-32"
                                    />
                                    <span className="text-sm font-mono">
                                        {(parsedResponse.confidence * 100).toFixed(1)}%
                                    </span>
                                </div>
                            </div>
                        )}

                        {parsedResponse.recommended_sort && (
                            <div>
                                <h4 className="font-semibold mb-2 text-sm">Recommended Sort:</h4>
                                <div className="bg-gray-100 p-3 rounded">
                                    <div className="flex items-center space-x-2 mb-2">
                                        <span className="text-sm font-mono bg-gray-200 px-2 py-1 rounded">
                                            {parsedResponse.recommended_sort.field}
                                        </span>
                                        <span className="text-sm font-mono bg-gray-200 px-2 py-1 rounded">
                                            {parsedResponse.recommended_sort.direction.toUpperCase()}
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-600">
                                        {parsedResponse.recommended_sort.reasoning}
                                    </p>
                                </div>
                            </div>
                        )}

                        {parsedResponse.suggested_questions &&
                            parsedResponse.suggested_questions.length > 0 && (
                                <div>
                                    <h4 className="font-semibold mb-2 text-sm">
                                        Followup Questions:
                                    </h4>
                                    <ul className="list-disc pl-5 space-y-1">
                                        {parsedResponse.suggested_questions.map(
                                            (question: string, index: number) => (
                                                <li key={index} className="text-sm">
                                                    {question}
                                                </li>
                                            ),
                                        )}
                                    </ul>
                                </div>
                            )}

                        {parsedResponse.query_suggestions &&
                            parsedResponse.query_suggestions.length > 0 && (
                                <div>
                                    <h4 className="font-semibold mb-2 text-sm">
                                        Query Suggestions:
                                    </h4>
                                    <div className="space-y-3">
                                        {parsedResponse.query_suggestions.map(
                                            (suggestion, index) => (
                                                <div
                                                    key={index}
                                                    className="bg-gray-50 p-3 rounded border border-gray-200"
                                                >
                                                    <div className="flex items-start justify-between mb-2">
                                                        <h5 className="font-medium text-sm text-gray-900">
                                                            {suggestion.suggestion}
                                                        </h5>
                                                        {onSuggestionClick && (
                                                            <Button
                                                                small
                                                                text="Try this"
                                                                intent={Intent.PRIMARY}
                                                                onClick={() =>
                                                                    onSuggestionClick(
                                                                        suggestion.suggestion,
                                                                    )
                                                                }
                                                                className="ml-2"
                                                            />
                                                        )}
                                                    </div>
                                                    <Pre className="text-xs bg-gray-100 p-2 rounded mb-2 overflow-x-auto">
                                                        {suggestion.modified_query}
                                                    </Pre>
                                                    <p className="text-xs text-gray-600">
                                                        {suggestion.reasoning}
                                                    </p>
                                                </div>
                                            ),
                                        )}
                                    </div>
                                </div>
                            )}
                    </div>
                ) : (
                    <div className="whitespace-pre-wrap text-sm">{message.content}</div>
                )}
            </Card>
        </div>
    )
}
